import { defineStore } from 'pinia';
import {
	useGetCheckoutQuery,
	useShippingChargesQuery,
	useGetPaymentProfileQuery,
	useGetTaxesQuery,
	usePlaceOrderMutation,
	useCompleteOrdeMutation,
} from '~/data/checkout.ts';
import { useLoganStore } from './logan';
import { useRoute } from 'vue-router';
import {
	getFinalTotalCost,
	getRewardsToBeRedeemed,
	getSubTotalNew,
	localeStringToNum,
	scrollToError,
} from '~/utils/checkout';
import { useCountryCode } from '~/stores/country';
import { StatsigSingleton } from '@laam/statsig-client';
import { useForm } from 'vee-validate';
import { CheckoutSchemaDefinitionNew } from '~/components/checkout/types';
import type { GiftingConfig } from '~/components/checkout/types';
import countries from '~/utils/countries.json';
import { useRewardsRatesQuery } from '~/data/rewards-rates';
import { useRewardsIndexQuery } from '~/data/rewards';
import { checkoutSubmit } from '~/utils/checkout-submit.ts';
import { checkoutHeaders } from '~/utils/checkout-headers.ts';

import type {
	LineItemsResponse,
	ShippingRatesResponse,
} from '~/types/Checkout';
import { useCurrency } from './currency';
import type { XpayRefType, StripeRefType } from '~/types/Checkout';
import { useGlobalStore } from './global';
import { useShippingDatesEstimator } from '~/data/shipping-date-estimator.ts';
import { useStatsigExperiment } from '~/utils/use-statsig-experiment';
import {
	COUPON_DISCOUNT_TYPE,
	COUPON_TYPE,
	SHIPPING_RESPONSE_CODES,
} from '~/utils/enums.ts';
import { useCouponsMutation } from '~/data/coupons';
import { useCheckoutMetaDataMutation } from '~/data/checkout';
import pkg from 'lodash';
import { useToast } from '@laam/ui/base';
import { PhCheckCircle, PhInfo } from '@phosphor-icons/vue';
const { isEqual } = pkg;

export const useCheckoutStore = defineStore('checkout', () => {
	const { toast } = useToast();

	const { store, storeType } = useGlobalStore();
	// defining variables that are needed
	const route = useRoute();
	const LoganStore = useLoganStore();
	const { loganId, showLoginSheet, email } = storeToRefs(LoganStore);
	const CurrencyStore = useCurrency();
	const { currencyCode } = storeToRefs(CurrencyStore);
	const { getFormattedPrice } = CurrencyStore;
	const { deliverToCountry } = storeToRefs(useCountryCode());
	const binDiscountObject = ref<{
		discountHash: string;
		binDiscount: number;
		campainId: number;
		binNumber: number | null;
	}>({
		discountHash: '',
		binDiscount: 0,
		campainId: 0,
		binNumber: 0,
	});
	const couponCodeDiscountInPkr = ref(0);
	const showPlaceOrderLoading = ref(false);
	const loading = ref(false);
	const showAddressValidationDialog = ref(false);
	const usingValidatedAddress = ref(false);
	const formattedAddress = ref();
	const callCouponValidate = ref(false);
	const inputsDisabled = ref(false);
	const shippingDates = ref<Record<string, string>>({});
	const maxShippingDate = ref('');
	const couponCodeAmount = ref(0);
	const jewelryInShippingCodes = ref([219, 419]);
	const showJewelrySummaryFlag = ref(true);
	const focusedField = ref<string | null>(null);
	const showCheckoutDialog = ref(false);
	const showEasyPaisaPopup = ref(false);
	const giftingConfig = ref<GiftingConfig>({
		show: false,
		title: '',
		subTitle: '',
		showNewBadge: false,
		messages: [],
		img: '',
		allowEmpty: false,
	});

	const disabledCitiesConfig = ref<{
		disabled: boolean;
		cities: string[];
		message: string;
	}>({
		disabled: false,
		cities: [],
		message: '',
	});

	const { isDeliveryDaysExperiment: deliveryDaysExperiment } =
		useStatsigExperiment();
	const deliveryMethodsExp = computed(() => {
		if (storeType === 'MARKETPLACE') {
			return true;
		}

		return false;
	});

	const isDeliveryDaysExperiment = computed(() => {
		if (deliveryDaysExperiment?.value) {
			return deliveryDaysExperiment.value;
		}
		return false;
	});

	const hash = ref('');
	// onMounted
	onMounted(() => {
		if (
			!deliveryMethodsExp.value ||
			(deliveryMethodsExp.value &&
				Array.isArray(shippingRates.value) &&
				shippingRates.value?.length === 1 &&
				shippingResponseCode.value != SHIPPING_RESPONSE_CODES.NO_SHIPPING)
		) {
			setFieldValue('deliveryMethod', 'STANDARD');
		}
		hash.value = toRaw(checkoutHash.value);
		const disabledCities = StatsigSingleton.getDynamicConfig('disabled_cities')
			.value as {
			disabled: boolean;
			cities: string[];
			message: string;
		};
		if (disabledCities) {
			disabledCitiesConfig.value.disabled = disabledCities.disabled;
			disabledCitiesConfig.value.cities = disabledCities.cities;
			disabledCitiesConfig.value.message = disabledCities.message;
		}
		const gifting = StatsigSingleton.getDynamicConfig('gifting-config')
			.value as unknown as GiftingConfig;
		if (gifting) {
			giftingConfig.value = gifting;
		}
	});
	const allowSaveAddress = ref(false);
	const billingAddressSame = ref(true);

	// defining vee-validate form
	const {
		values,
		handleSubmit,
		setFieldValue,
		resetField,
		errors,
		resetForm,
		setErrors,
		validateField,
		setFieldError,
	} = useForm({
		validationSchema: CheckoutSchemaDefinitionNew,
		keepValuesOnUnmount: true,
		initialValues: {
			// if countries object doesnt contain cookie country then fallback to PK
			countryCode: !Object.keys(countries).includes(deliverToCountry.value!)
				? 'PK'
				: deliverToCountry.value ?? 'PK',
			// phone number data
			contactInformation: {
				email: LoganStore.isSignedIn ? LoganStore.email || '' : '',
				phonePlaceholderPattern: '(0300 1234567)',
				phoneNumber: '',
			},
			shippingAddress: {
				firstName: LoganStore.isSignedIn ? LoganStore.firstName || '' : '',
				lastName: LoganStore.isSignedIn ? LoganStore.lastName || '' : '',
				address1: '',
				address2: '',
				city: '',
				zip: '',
				province: '',
			},
			paymentMethod: '',
			billingAddressType: 'same',
			checkoutLoading: false,
			usingRewards: false,
			xpayFormReady: true,
			stripeFormReady: true,
			couponCode: '',
			couponType: '',
			couponDiscountType: '',
			deliveryMethod: '',
			isOOSItems: false,
		},
	});

	// computed fields that we need
	const checkoutHash = computed(() => route.params.chash as string);
	const countryCodeRef = computed(() => values.countryCode as string);
	const zipCodeRef = computed(() => values.shippingAddress!.zip as string);
	const stateRef = computed(() => values.shippingAddress!.province as string);
	const stateName = computed(() => {
		const country = countries[countryCodeRef.value as keyof typeof countries];
		const zones = country && 'zones' in country ? country.zones : [];
		return zones.find((zone) => zone.code === stateRef.value)?.name ?? '';
	});
	const isOOSItems = computed(() => values.isOOSItems as boolean);
	const couponType = computed(() => values.couponType as string);
	const couponDiscountType = computed(
		() => values.couponDiscountType as string,
	);
	const showDisabledCityMessage = computed(() => {
		return (
			disabledCitiesConfig.value.disabled &&
			disabledCitiesConfig.value.cities.includes(
				values?.shippingAddress?.city?.toLowerCase() ?? '',
			)
		);
	});
	const oosScrolledIntoView = ref<boolean>(false);
	const jewelryScrolledIntoView = ref(false);
	// const oosItems = ref<LineItemsResponse>([]);
	// get checkout query, will return lineitems
	const {
		data,
		isLoading: checkoutDataLoading,
		suspense,
		refetch: refetchCheckout,
	} = useGetCheckoutQuery(checkoutHash, loganId, store.id.toString());
	// checkout data computed field, fitlers out out of stock items.
	// returns available line items only
	const checkoutData = computed(() => {
		const inStockLineitems: LineItemsResponse = [];
		data.value?.line_items.forEach((item) => {
			if (item.variant_inventory >= item.quantity || !item.out_of_stock) {
				inStockLineitems.push(item);
			}
		});
		return inStockLineitems;
	});
	const productIds = computed(() => {
		return checkoutData.value.map((item) => item.product_id);
	});
	const oosItems = computed(() => {
		const oosLineItems: LineItemsResponse = [];
		data.value?.line_items.forEach((item) => {
			if (item.out_of_stock) {
				oosLineItems.push(item);
			}
		});

		return oosLineItems;
	});

	const refetchGetCheckout = () => {
		refetchCheckout();
	};

	const eidGiftEnabled = computed(() => {
		return giftingConfig.value.show;
	});

	// generates payload needed in multiple queries
	const lineItemsPayload = computed(() => {
		if (!checkoutData.value) {
			return undefined;
		}

		const lineItems = checkoutData.value.map((item: any) => ({
			id: item.id,
			quantity: item.quantity,
		}));

		if (lineItems.length === 0) {
			return undefined;
		}
		return { line_items: lineItems };
	});
	const { mutate } = useCheckoutMetaDataMutation(loganId, countryCodeRef);
	// shipping dates query
	const variantId = ref('');
	const { data: shippingDate, suspense: shippingDateSuspense } =
		useShippingDatesEstimator(variantId);

	const potentialRewards = computed(() => data.value?.potential_rewards);
	// shipping rates query, uses query keys to automatically fetch new values if key is changed
	const {
		data: shippingRates,
		isLoading: shippingLoading,
		suspense: shippingSuspense,
	} = useShippingChargesQuery(
		countryCodeRef,
		loganId,
		lineItemsPayload,
		checkoutHash,
		store.id.toString(),
		checkoutDataLoading,
		deliveryMethodsExp,
		stateName,
	);
	// tax rates query, will get taxes for select us states. Uses query keys to automatically fetch new values if key changed
	const { data: taxRates, isLoading: taxLoading } = useGetTaxesQuery(
		countryCodeRef,
		loganId,
		checkoutHash,
		zipCodeRef,
		stateRef,
		store.id.toString(),
		checkoutDataLoading,
	);

	// payment profile query, fetches payment methods
	const { data: paymentProfilesList } = useGetPaymentProfileQuery(
		countryCodeRef,
		loganId,
		lineItemsPayload,
		checkoutHash,
		store.id.toString(),
		currencyCode,
		checkoutDataLoading,
	);

	// rewards query
	const { data: rewardsRatesData } = useRewardsRatesQuery();
	const { data: rewardsIndexData } = useRewardsIndexQuery();

	const couponHeaders = computed(() => {
		return {
			'is-app': localStorage.getItem('isApp') ? 'true' : 'false',
			'is-savyour': !!useCookie('sav_aff_info').value + '',
		};
	});
	// coupons mutation
	const { mutateAsync: getCouponData } = useCouponsMutation(couponHeaders);

	// computed field to calculate lineitem subtotal in PKR
	const subTotalInPkr = computed(() =>
		getSubTotalNew(
			checkoutData.value,
			LoganStore.isHvc,
			false, //flag given false as it will always calculate values in pkr
			undefined,
			undefined,
		),
	);

	// computed field to calculate lineitem subtotal in native currency
	const subTotal = computed(() =>
		getSubTotalNew(
			checkoutData.value,
			LoganStore.isHvc,
			true,
			getFormattedPrice,
			currencyCode.value,
		),
	);

	// remove this once done
	const headers = computed(() => {
		return checkoutHeaders(
			deliverToCountry.value,
			email?.value ?? '',
			loganId?.value ?? '',
		);
	});

	const selectedShipping = computed(() => {
		if (
			deliveryMethodsExp.value &&
			shippingRates.value &&
			Array.isArray(shippingRates.value)
		) {
			if (shippingRates.value?.length > 1 && deliveryMethodsExp.value) {
				if (values.deliveryMethod) {
					return shippingRates.value?.filter(
						(item) => item.type === values.deliveryMethod,
					)[0];
				}
			} else {
				return shippingRates.value?.filter(
					(item) => item.type === 'STANDARD',
				)[0];
			}
		} else if (
			!deliveryMethodsExp.value &&
			shippingRates.value &&
			Object.keys(shippingRates.value).length > 0
		) {
			return shippingRates.value as {
				shipping_price: number;
				original_price: number;
			};
		}
		return null;
	});
	const shippingResponseCode = computed(() => {
		if (
			deliveryMethodsExp.value &&
			shippingRates.value &&
			Array.isArray(shippingRates.value) &&
			shippingRates.value.length === 1
		) {
			return (shippingRates.value[0] as ShippingRatesResponse[0]).status;
		}
		return null;
	});
	const jewelryLineItems = computed(() => {
		if (
			shippingResponseCode.value &&
			shippingRates.value &&
			Array.isArray(shippingRates.value) &&
			shippingRates.value.length === 1 &&
			jewelryInShippingCodes.value.includes(shippingResponseCode.value) &&
			uniqueItemsCount.value !== 1
		) {
			return (shippingRates.value[0]! as ShippingRatesResponse[0]).line_items;
		}
		return [];
	});
	const showJewelrySummary = computed(() => {
		if (shippingResponseCode.value === SHIPPING_RESPONSE_CODES.NO_PREMIUM) {
			if (uniqueItemsCount.value === 1) {
				return false;
			}
			return showJewelrySummaryFlag.value; // if flag is set to false dont show, can be dismissed
		} else if (
			shippingResponseCode.value === SHIPPING_RESPONSE_CODES.NO_SHIPPING
		) {
			return true; // show
		}
		return false;
	});
	const noShippingAvailable = computed(
		() => shippingResponseCode.value === SHIPPING_RESPONSE_CODES.NO_SHIPPING,
	);

	// check to see if shipping price is 0 because of free shipping
	// second case is when shipping method is not selected, in that case this is going to be false
	const isFreeShipping = computed(
		() =>
			selectedShipping.value &&
			selectedShipping.value?.original_price === 0 &&
			selectedShipping.value?.shipping_price === 0,
	);

	// fetches shipping price in native currency
	const shipping = computed(() => {
		if (
			selectedShipping.value &&
			selectedShipping.value?.shipping_price! >= 0
		) {
			return localeStringToNum(
				getFormattedPrice(
					selectedShipping.value?.shipping_price!, //convert shipping to native currency without rounding
					true,
					false,
				),
			);
		} else return 0;
	});

	// fetches shipping price in pkr
	const shippingInPkr = computed(() => {
		if (selectedShipping.value && selectedShipping.value?.shipping_price!) {
			return selectedShipping.value?.shipping_price!;
		} else return 0;
	});

	// calculates tax on the order
	const tax = computed(() => {
		if (values.countryCode !== 'US') {
			return 0;
		}
		if (taxRates.value && taxRates.value.tax_rate > 0) {
			return (shipping.value! + subTotal.value!) * taxRates.value.tax_rate!;
		}
		return 0;
	});
	// calculates tax in pkr
	const taxInPkr = computed(() => {
		if (values.countryCode !== 'US') {
			return 0;
		}
		if (taxRates.value && taxRates.value.tax_rate > 0) {
			return (
				(shippingInPkr.value! + subTotalInPkr.value!) * taxRates.value.tax_rate!
			);
		}
		return 0;
	});
	// calculates final total ie shippig, tax, subtotal and credit cart discount
	// calculated in native currency
	const finalTotal = computed(() =>
		parseFloat(
			getFinalTotalCost(
				subTotal.value!,
				shipping.value!,
				tax.value!,
				binDiscount.value!,
			).toFixed(2),
		),
	);
	// calculates final total ie shippig, tax, subtotal and credit cart discount
	// calculated in pkr currency
	const finalTotalInPkr = computed(() =>
		parseFloat(
			getFinalTotalCost(
				subTotalInPkr.value!,
				shippingInPkr.value!,
				taxInPkr.value!,
				binDiscount.value!,
			).toFixed(2),
		),
	);
	// this will calculate coupon code discount in native currency
	// using rewards rates to convert pkr discount to native currency
	const couponCodeDiscount = computed(() => {
		if (LoganStore.isSignedIn && finalTotal.value) {
			if (couponType.value == COUPON_TYPE.PERCENTAGE) {
				// For Percentage, discount value is already in native currency.
				return couponCodeDiscountInPkr.value;
			}
			return localeStringToNum(
				getFormattedPrice(
					couponCodeDiscountInPkr.value, //convert discount to native currency without rounding
					true,
					false,
				),
			);
		}
		return 0;
	});
	// if shipping or taxes are being fetched this flag is set to true, used to disable place order button
	const pricesLoading = computed(
		() => shippingLoading.value || taxLoading.value,
	);
	// calculates rewards balance and rewards that can be used
	const rewardsBalance = computed(() => rewardsIndexData?.value?.balance);
	const rewardsToBeRedeemed = computed(() => {
		if (LoganStore.isSignedIn && finalTotal.value && rewardsBalance.value) {
			if (rewardsRatesData.value) {
				const coinsInNative =
					rewardsBalance.value! *
					rewardsRatesData.value![currencyCode.value]!.coins_to_native;
				return getRewardsToBeRedeemed(
					coinsInNative as number,
					finalTotal.value,
				);
			} else {
				return getRewardsToBeRedeemed(
					rewardsBalance.value as number,
					finalTotal.value,
				);
			}
		}
		return 0;
	});
	const rewardsToBeRedeemedInPkr = computed(() => {
		if (
			LoganStore.isSignedIn &&
			finalTotalInPkr.value &&
			rewardsBalance.value
		) {
			if (rewardsRatesData.value) {
				const coinsInNative =
					rewardsBalance.value! *
					rewardsRatesData.value!['pkr']!.coins_to_native;
				return parseFloat(
					getRewardsToBeRedeemed(
						coinsInNative as number,
						finalTotalInPkr.value,
					).toFixed(2),
				);
			}
		}
		return 0;
	});
	const rewardsEnabled = computed(
		() => LoganStore.isSignedIn && !LoganStore.isHvc && isRewardsEnabled(),
	);

	const grandTotal = computed(() => {
		// if above condition is false and only using rewards, subtract rewards only
		if (values.usingRewards) {
			return finalTotal.value - rewardsToBeRedeemed.value;
		}
		// if above conditions are false and couponCode discount is not null or undefined and has value greater than 0
		// subtract coupon code discount
		else if (couponCodeDiscount.value && couponCodeDiscount.value > 0) {
			return finalTotal.value - couponCodeDiscount.value;
		}
		// if all above conditons are invalid just return final total
		else {
			return finalTotal.value;
		}
	});
	const grandTotalInPkr = computed(() => {
		// if using rewards and couponCode discount is not null or undefined and has value greater than 0
		// this means both discounts are applicable, subtract both from finalTotal in Pkr
		if (
			values.usingRewards &&
			couponCodeDiscountInPkr.value &&
			couponCodeDiscountInPkr.value > 0
		) {
			return (
				finalTotalInPkr.value -
				rewardsToBeRedeemedInPkr.value -
				couponCodeDiscountInPkr.value
			);
		}
		// if above condition is false and only using rewards, subtract rewards only
		else if (values.usingRewards) {
			return finalTotalInPkr.value - rewardsToBeRedeemedInPkr.value;
		}
		// if above conditions are false and couponCode discount is not null or undefined and has value greater than 0
		// subtract coupon code discount
		else if (
			couponCodeDiscountInPkr.value &&
			couponCodeDiscountInPkr.value > 0
		) {
			return finalTotalInPkr.value - couponCodeDiscountInPkr.value;
		}
		// if all above conditons are invalid just return final total in pkr
		else {
			return finalTotalInPkr.value;
		}
	});
	const discountObject = computed(() => {
		interface ObjType {
			rewards?: {
				description: string;
				amount: string;
				code: string;
				type: string;
			};
			coupon_code?: {
				description: string;
				amount: string;
				code: string;
				type: string;
			};
			credit_card_discount?: {
				description: string;
				amount: string;
				code: string;
				type: string;
			};
		}
		const obj: ObjType = {};
		if (rewardsEnabled && values.usingRewards) {
			const rewards = {
				description: 'Rewards',
				amount: convertRewardsToCoins(rewardsToBeRedeemed.value).toString(),
				code: 'rewards',
				type: 'rewards',
			};
			obj.rewards = rewards;
		}
		if (couponCodeDiscount.value > 0) {
			const coupons = {
				description: 'Coupon Code',
				amount:
					couponType.value === COUPON_TYPE.SHIPPING
						? '0'
						: couponCodeDiscount.value.toString(),
				code: values.couponCode!.toLowerCase(),
				type: 'coupon',
			};
			obj.coupon_code = coupons;
		}
		// if credit card discount is applied
		if (binDiscount.value > 0) {
			const binDiscountObj = {
				description: 'Card Discount',
				amount: binDiscount.value.toString(),
				code: 'Discount',
				type: 'fixed_amount',
			};
			obj.credit_card_discount = binDiscountObj;
		}
		return obj;
	});
	const shippingAddress = computed(() => values.shippingAddress);
	const contactInformation = computed(() => values.contactInformation);
	const billingAddress = computed(() => values.billingAddress);

	const itemsCount = computed(() =>
		checkoutData.value.reduce((sum, item) => sum + item.quantity, 0),
	);
	const uniqueItemsCount = computed(() => checkoutData.value.length);

	const excludedIds = computed(
		() => jewelryLineItems.value?.map((item) => item.id) ?? [],
	);
	const binDiscount = computed(() => binDiscountObject.value.binDiscount);
	const updateBinDiscount = (discount: {
		discountHash: string;
		binDiscount: number;
		campainId: number;
		binNumber: number | null;
	}) => {
		binDiscountObject.value = discount;
	};
	const updateCouponCodeDiscountInPkr = (
		value: number,
		couponType?: string,
		discountType?: string,
	) => {
		if (couponType === COUPON_TYPE.SHIPPING) {
			if (discountType === COUPON_DISCOUNT_TYPE.PERCENTAGE) {
				couponCodeDiscountInPkr.value = (shippingInPkr.value / 100) * value;
			} else {
				couponCodeDiscountInPkr.value = value;
			}
		} else {
			couponCodeDiscountInPkr.value = value;
		}
	};
	const setPlaceOrderLoading = (value: boolean) => {
		showPlaceOrderLoading.value = value;
	};
	const setLoading = (value: boolean) => {
		loading.value = value;
		if (value === false) {
			toggleEasyPaisaPopup(false);
			setPlaceOrderLoading(false);
		}
	};
	const updateAddressValidationDialog = () => {
		showAddressValidationDialog.value = !showAddressValidationDialog.value;
	};
	const closeAddressValidationDialog = () => {
		if (showAddressValidationDialog.value) {
			showAddressValidationDialog.value = false;
		}
	};
	const convertRewardsToCoins = (rewardsToBeRedeemed: number) => {
		if (LoganStore.isSignedIn && rewardsRatesData.value) {
			return (
				rewardsToBeRedeemed /
				rewardsRatesData.value![currencyCode.value]!.coins_to_native //getting rewards back to coins from native currency
			);
		}
		return rewardsToBeRedeemed;
	};
	const paymentMethod = computed(() => {
		if (usingWallet.value && XPAY.includes(values.paymentMethod!)) {
			return values.paymentMethod;
		}
		return values.paymentMethod;
	});
	const usingWallet = computed<boolean>(() => {
		const isWalletMethod =
			values.paymentMethod === 'xpay-jazz' ||
			values.paymentMethod === 'xpay-easypaisa';
		return isWalletMethod;
	});
	const isInternationalCheckout = computed(() => countryCodeRef.value !== 'PK');
	const { mutateAsync, isPending } = usePlaceOrderMutation(
		loganId,
		store.id.toString(),
	);

	const { mutateAsync: completeOrderAsync } = useCompleteOrdeMutation(
		loganId,
		store.id.toString(),
	);

	const updateShowCheckoutDialog = () => {
		showCheckoutDialog.value = !showCheckoutDialog.value;
	};

	const toggleEasyPaisaPopup = (show: boolean) => {
		showEasyPaisaPopup.value = show;
	};
	// place order function
	// this functions calls handleSubmit of the form which in turn calls checkout submit function
	const placeOrder = (
		xpayRef: (args: XpayRefType) => void,
		stripeRef: (args: StripeRefType) => void,
		gaClientId: string,
	) => {
		if (showDisabledCityMessage.value) {
			updateShowCheckoutDialog();
			return;
		}

		handleSubmit(
			() => {
				if (values.paymentMethod === 'xpay-easypaisa') {
					toggleEasyPaisaPopup(true);
				}
				checkoutSubmit(xpayRef, stripeRef, mutateAsync, gaClientId, isPending);
			},
			() => {
				const keys = Object.keys(errors.value || {});
				scrollToError(keys);
				if (keys.includes('isOOSItems')) {
					oosScrolledIntoView.value = true;
				}
				if (
					keys.includes('deliveryMethod') &&
					shippingResponseCode.value === SHIPPING_RESPONSE_CODES.NO_SHIPPING
				) {
					triggerJewlleryScroll();
				}
			},
		)();
	};
	const updateInputDisabled = (value: boolean) => {
		inputsDisabled.value = value;
	};
	const populateShippingDates = async () => {
		// Ensure checkoutData is defined
		if (checkoutData.value) {
			for (const item of checkoutData.value) {
				variantId.value = item.id.toString();

				await shippingDateSuspense();

				shippingDates.value[variantId.value] = dateFormatter(
					shippingDate.value?.handling_date!,
				);

				if (!maxShippingDate.value) {
					maxShippingDate.value = shippingDate.value?.handling_date!;
				} else if (shippingDate.value?.handling_date! > maxShippingDate.value) {
					maxShippingDate.value = shippingDate.value?.handling_date!;
				}
			}
			maxShippingDate.value = dateFormatter(maxShippingDate.value);
		}
	};
	const updateAllowSaveAddress = () => {
		allowSaveAddress.value = !allowSaveAddress.value;
	};
	const invalidCouponMessage = ref('');
	const showInvalidCouponDialog = ref(false);

	const validateCoupon = async (couponCode: string) => {
		if (!LoganStore.isSignedIn) {
			callCouponValidate.value = true;
			LoganStore.setRequiresSignIn();
			return;
		}
		if (
			values.contactInformation?.email === null ||
			values.contactInformation?.email === undefined ||
			values.contactInformation?.email === ''
		) {
			invalidCouponMessage.value =
				'Please enter email address to apply coupon.';
			showInvalidCouponDialog.value = true;
			return;
		}
		const payload = {
			buyer_id: LoganStore.loganId,
			coupon_code: couponCode,
			country_code: countryCodeRef.value!,
			order_total: subTotal.value,
			email: values.contactInformation?.email,
			shipping_method: values.deliveryMethod,
			line_items: checkoutData.value,
			base_currency_code: currencyCode.value,
		};
		try {
			const data = await getCouponData(payload);
			if ('success' in data) {
				const toastShown = ref(false);
				if (data.success.type === 'SHIPPING' && !values.deliveryMethod) {
					showInvalidCouponDialog.value = true;
					invalidCouponMessage.value =
						'Please select a delivery method before applying.';
					return;
				}
				if (values.usingRewards) {
					setFieldValue('usingRewards', false);
					toastShown.value = true;
					toast({
						title:
							'The coupon has been applied. Your previously applied rewards have been disabled since only one discount can be used at a time',
						variant: 'info',
						icon: h(PhInfo, {
							size: 20,
						}),
						duration: 5000,
						class: 'lg:w-[50%] w-[90%]',
					});
				}
				if (binDiscount.value) {
					toastShown.value = true;
					updateBinDiscount({
						discountHash: '',
						binDiscount: 0,
						campainId: 0,
						binNumber: 0,
					});
					toast({
						title: `The coupon has been applied. Your previously applied ${usingWallet.value ? 'JazzCash' : 'Bank Alfalah'} discount has been removed since only one discount can be used at a time`,
						variant: 'info',
						icon: h(PhInfo, {
							size: 20,
						}),
						duration: 5000,
						class: 'lg:w-[50%] w-[90%]',
					});
				}
				if (!toastShown.value) {
					toast({
						title: 'Coupon added successfully!',
						variant: 'info',
						icon: h(PhCheckCircle, {
							color: 'white',
							size: 24,
						}),
						duration: 3000,
					});
				}
				updateCouponCodeDiscountInPkr(
					data.success.discount,
					data.success.type,
					data.success.discount_type,
				);
				couponCodeAmount.value = data.success.discount; // stores percentage or flat amount
				setFieldValue('couponCode', couponCode);
				setFieldValue('couponType', data.success.type);
				setFieldValue('couponDiscountType', data.success.discount_type);
				return data;
			} else {
				// this check is for removing discount if subtotal amount decreases than minimum limit
				if (values.couponCode) {
					removeCouponDiscount();
					invalidCouponMessage.value = data.detail.error;
					showInvalidCouponDialog.value = true;
					return data;
				}
				setFieldValue('couponCode', '');
				setFieldValue('couponType', '');
				setFieldValue('couponDiscountType', '');
				updateCouponCodeDiscountInPkr(0);
				invalidCouponMessage.value = data.detail.error;
				showInvalidCouponDialog.value = true;
				return data;
			}
		} catch (error) {
			showInvalidCouponDialog.value = true;
			return error;
		}
	};
	const removeCouponDiscount = () => {
		setFieldValue('couponCode', '');
		setFieldValue('couponType', '');
		setFieldValue('couponDiscountType', '');
		updateCouponCodeDiscountInPkr(0);
	};

	const handleRewardsToggle = (usingRewards: boolean) => {
		if (usingRewards && values.couponCode) {
			removeCouponDiscount();
			toast({
				title:
					'Rewards have been enabled. Your previously applied coupon has been removed since only one discount can be used at a time',
				variant: 'info',
				icon: h(PhInfo, {
					size: 20,
				}),
				duration: 5000,
				class: 'lg:w-[50%] w-[90%]',
			});
		}
	};

	const reset = () => {
		binDiscountObject.value = {
			binDiscount: 0,
			discountHash: '',
			campainId: 0,
			binNumber: 0,
		};
		couponCodeDiscountInPkr.value = 0;
		showPlaceOrderLoading.value = false;
		loading.value = false;
		showAddressValidationDialog.value = false;
		usingValidatedAddress.value = false;
		formattedAddress.value = null;
		inputsDisabled.value = false;
		allowSaveAddress.value = false;
		resetForm();
		setErrors({
			shippingAddress: undefined,
			contactInformation: undefined,
			paymentMethod: undefined,
		});
	};
	const triggerJewlleryScroll = () => {
		if (
			jewelryInShippingCodes.value.includes(shippingResponseCode.value ?? 200)
		) {
			jewelryScrolledIntoView.value = true;
			let id = 'jewelry-no-shipping';
			if (shippingResponseCode.value === SHIPPING_RESPONSE_CODES.NO_PREMIUM) {
				id = 'jewelry-no-premium';
			}
			document
				.getElementById(id)
				?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
			setTimeout(() => {
				jewelryScrolledIntoView.value = false;
			}, 3000);
		}
	};

	const updateShowJewlrySummaryFlag = () => {
		showJewelrySummaryFlag.value = !showJewelrySummaryFlag.value;
	};
	const handleFocus = (field: string) => {
		focusedField.value = field;
	};
	const oldObj = ref({
		hash: '',
		body: {
			hash: '',
			email: '',
			shipping_address: {
				zip: '',
				city: '',
				phone: '',
				country: '',
				address1: '',
				address2: '',
				last_name: '',
				province: '',
				first_name: '',
			},
		},
	});
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	const saveCheckoutData = () => {
		const trimmedPhNumber = getTrimmedPhoneNumber(
			contactInformation.value?.phoneNumber!,
			countryCodeRef.value!,
		);
		const phoneNumber =
			countries[
				countryCodeRef.value as keyof typeof countries
			].phone[0]?.toString()! + trimmedPhNumber;
		let provinceName = '';
		if (shippingAddress.value?.province) {
			const country = countries[values.countryCode as keyof typeof countries];
			const zones: {
				name: string;
				code: string;
			}[] = country && 'zones' in country ? country.zones : [];
			provinceName = getProvinceName(
				values.shippingAddress?.province!,
				zones,
			)?.name!;
		}
		const shippingAddressObj = {
			zip: shippingAddress.value!.zip as string,
			city:
				countryCodeRef.value === 'AE' && provinceName
					? provinceName
					: (shippingAddress.value!.city as string),
			phone: phoneNumber as string,
			country: countryCodeRef.value!,
			address1: shippingAddress.value!.address1 as string,
			address2: shippingAddress.value!.address2 as string,
			last_name: shippingAddress.value!.lastName as string,
			province: shippingAddress.value!.province as string,
			first_name: shippingAddress.value!.firstName as string,
		};
		const obj = {
			hash: hash.value,
			body: {
				hash: hash.value,
				shipping_address: shippingAddressObj!,
				email: contactInformation.value?.email!,
			},
		};
		if (isEqual(obj, oldObj.value)) {
			return;
		}
		oldObj.value = obj;
		mutate(obj);
	};
	const handleBlur = () => {
		focusedField.value = null;
		if (timeoutId) clearTimeout(timeoutId);
		saveCheckoutData();
	};

	watch([values.shippingAddress, values.contactInformation], () => {
		// Clear the previous timeout
		if (timeoutId) clearTimeout(timeoutId);

		// Set a new timeout for 10 minutes
		timeoutId = setTimeout(() => {
			saveCheckoutData();
		}, 600000); // 10 minutes
	});

	// we have integrated 2 login touch points on checkout
	// One is for coupon code
	// Second is for rewards
	// Incase of coupon code, we validate coupon code after login, callCouponValidate is set to true in that case
	// if user closes sign in sheet without logging in, then this watcher will detect it and set callCouponValidate to false
	// add more such flags here to reset the state
	watch(
		() => showLoginSheet.value,
		(newValue, oldValue) => {
			if (oldValue === true && newValue === false) {
				callCouponValidate.value = false;
			}
		},
	);

	watch(
		() => values.usingRewards,
		(newValue) => {
			if (newValue && values.couponCode) {
				handleRewardsToggle(true);
			}
		},
	);

	return {
		checkoutData,
		checkoutDataLoading,
		subTotalInPkr,
		subTotal,
		lineItemsPayload,
		headers,
		values,
		handleSubmit,
		errors,
		shippingRates,
		shippingLoading,
		shippingSuspense,
		paymentProfilesList,
		taxRates,
		taxLoading,
		paymentMethod,
		shipping,
		shippingInPkr,
		tax,
		taxInPkr,
		finalTotal,
		finalTotalInPkr,
		binDiscount,
		zipCodeRef,
		stateRef,
		countryCodeRef,
		couponCodeDiscountInPkr,
		couponCodeDiscount,
		rewardsRatesData,
		pricesLoading,
		showPlaceOrderLoading,
		rewardsIndexData,
		rewardsBalance,
		rewardsToBeRedeemed,
		rewardsToBeRedeemedInPkr,
		rewardsEnabled,
		loading,
		showAddressValidationDialog,
		usingValidatedAddress,
		grandTotal,
		grandTotalInPkr,
		formattedAddress,
		checkoutHash,
		discountObject,
		shippingAddress,
		contactInformation,
		billingAddress,
		callCouponValidate,
		potentialRewards,
		inputsDisabled,
		shippingDates,
		maxShippingDate,
		isInternationalCheckout,
		allowSaveAddress,
		billingAddressSame,
		invalidCouponMessage,
		showInvalidCouponDialog,
		itemsCount,
		deliveryMethodsExp,
		isFreeShipping,
		suspense,
		setFieldValue,
		resetField,
		updateBinDiscount,
		updateCouponCodeDiscountInPkr,
		setPlaceOrderLoading,
		setLoading,
		updateAddressValidationDialog,
		closeAddressValidationDialog,
		convertRewardsToCoins,
		placeOrder,
		reset,
		validateField,
		setFieldError,
		updateInputDisabled,
		populateShippingDates,
		oosItems,
		refetchGetCheckout,
		isOOSItems,
		oosScrolledIntoView,
		eidGiftEnabled,
		couponType,
		couponDiscountType,
		updateAllowSaveAddress,
		validateCoupon,
		removeCouponDiscount,
		couponCodeAmount,
		shippingResponseCode,
		jewelryLineItems,
		jewelryInShippingCodes,
		jewelryScrolledIntoView,
		triggerJewlleryScroll,
		excludedIds,
		showJewelrySummary,
		showJewelrySummaryFlag,
		updateShowJewlrySummaryFlag,
		noShippingAvailable,
		uniqueItemsCount,
		isPending,
		handleFocus,
		handleBlur,
		focusedField,
		usingWallet,
		binDiscountObject,
		productIds,
		disabledCitiesConfig,
		showDisabledCityMessage,
		updateShowCheckoutDialog,
		showCheckoutDialog,
		showEasyPaisaPopup,
		toggleEasyPaisaPopup,
		giftingConfig,
		isDeliveryDaysExperiment,
		handleRewardsToggle,
		completeOrderAsync,
	};
});
